import {
  ALWAYS_VISIBLE_MENU,
  MENU_UI,
  MODULE_UI,
} from '@/constants/menuCatalog';
import type { IntlShape } from '@/i18n';
import type { PlanModuleGateRowVO } from '@/types';

/**
 * 模块门控的纯函数：路径 → 模块、行 × 模块 → 是否锁 / 锁的原因、原因 → 人话。
 *
 * 判定只认服务端那一格 `available`（platform 的 `moduleAvailable`），前端不自己比档位序，
 * 也不按权限码推。取不到整份数据时一律按「全亮」放行——与网关「判定服务不可用时放行」同口径。
 */

/** 模块码 → 平台登记的那一行；键是 `moduleCode` */
export type ModuleGateRows = Record<string, PlanModuleGateRowVO>;

/** 服务端的原因码；前三个是本接口当前会产生的那三档，其余码位按未知处理 */
export const MODULE_LOCK_REASON = {
  planTooLow: 'plan_too_low',
  phaseNotReady: 'phase_not_ready',
  disabled: 'module_disabled',
} as const;

/**
 * 两种锁要分开说：升套餐能解决的，与「谁都还用不了」的出路完全不同（PRD `gateReason`）。
 * 平台关掉与未知码也单列，不能回落成「需升级」——那会编造具体原因。
 */
export type ModuleGateReason =
  | { kind: 'plan'; planCode: string; planName: string | null }
  | { kind: 'phase'; phase: string }
  | { kind: 'disabled' }
  | { kind: 'unknown'; code: string };

const PHASE_1 = 'phase_1';

/**
 * 期次是否已经开放：目前只有 `phase_1` 算到了，其余档（二三期）套餐再高也用不了。
 * 导航原因与套餐页三态共用这一处，不在两个文件各写一份 `=== 'phase_1'`。
 */
export function isCurrentPhase(phase: string): boolean {
  return phase === PHASE_1;
}

/**
 * 路径 → 模块的兜底表，给首段不是模块码的页用。
 *
 * 目前只有 `sys.package`（`/iam/package`，10-09 §1.2 规定的例外地址）；其余页面首段
 * 就是 `MODULE_UI` 的模块码。归属模块与 `useMenu` 同一判据（`module ?? 键前缀`）。
 */
const PATH_MODULE: Record<string, string> = {};
const rememberPath = (path: string | undefined, moduleCode: string | undefined) => {
  if (path && moduleCode && MODULE_UI[moduleCode]) PATH_MODULE[path] = moduleCode;
};
Object.entries(MENU_UI).forEach(([key, ui]) =>
  rememberPath(ui.path, ui.module ?? key.split('.')[0]),
);
ALWAYS_VISIBLE_MENU.forEach((item) => rememberPath(item.path, item.module));

const PATH_MODULES_BY_LENGTH = Object.keys(PATH_MODULE).sort(
  (a, b) => b.length - a.length,
);

/**
 * 当前地址属于哪个一级模块；`/home`、`/profile`、`/iframe` 这类不归任何模块的返回 undefined。
 *
 * 首段是七个模块码之一直接命中（详情页 `/member/person-list/PSN1` 也走这条）；
 * 否则查一次兜底表，`/iam/package` 因此仍归到 `sys`。
 */
export function moduleOfPath(pathname: string): string | undefined {
  const first = pathname.split('/').filter(Boolean)[0];
  if (first && MODULE_UI[first]) return first;
  const path = pathname.replace(/\/+$/, '') || '/';
  const hit = PATH_MODULES_BY_LENGTH.find(
    (known) => path === known || path.startsWith(`${known}/`),
  );
  return hit ? PATH_MODULE[hit] : undefined;
}

/** 这个模块当前是否锁着；数据没取到、行不存在都算没锁（全亮放行） */
export function isModuleGated(
  rows: ModuleGateRows,
  moduleCode?: string | null,
): boolean {
  if (!moduleCode) return false;
  const row = rows[moduleCode];
  return Boolean(row && !row.available);
}

/**
 * 锁的原因；模块可用或没有这一行时返回 null。
 *
 * 优先读服务端的 `lockedReason`；旧后端不返回这个字段时，按 phase / 档位回落。
 */
export function moduleGateReasonOf(
  rows: ModuleGateRows,
  moduleCode?: string | null,
): ModuleGateReason | null {
  if (!moduleCode) return null;
  const row = rows[moduleCode];
  if (!row || row.available) return null;
  const code = row.lockedReason;
  if (code === MODULE_LOCK_REASON.phaseNotReady) {
    return { kind: 'phase', phase: row.phase };
  }
  if (code === MODULE_LOCK_REASON.planTooLow) {
    return {
      kind: 'plan',
      planCode: row.minPlan.code,
      planName: row.minPlan.name,
    };
  }
  if (code === MODULE_LOCK_REASON.disabled) return { kind: 'disabled' };
  if (code) return { kind: 'unknown', code };
  if (!isCurrentPhase(row.phase)) return { kind: 'phase', phase: row.phase };
  return { kind: 'plan', planCode: row.minPlan.code, planName: row.minPlan.name };
}

/** `phase_2` → `'2'`；枚举换写法时返回 null，交给通用「暂未开放」文案兜底 */
export function phaseOrdinalOf(phase: string): string | null {
  const match = /^phase_(\d+)$/.exec(phase);
  return match ? match[1] : null;
}

/** 原因 → 界面文案：拦截页与菜单点击提示共用这一份，不各拼一套 */
export function moduleGateReasonText(
  intl: IntlShape,
  reason: ModuleGateReason | null,
): string {
  if (!reason) return intl.formatMessage({ id: 'layout.moduleGate.unavailable' });
  if (reason.kind === 'disabled') {
    return intl.formatMessage({ id: 'layout.moduleGate.disabledReason' });
  }
  if (reason.kind === 'unknown') {
    return intl.formatMessage({ id: 'layout.moduleGate.unavailable' });
  }
  if (reason.kind === 'phase') {
    const phase = phaseOrdinalOf(reason.phase);
    return phase
      ? intl.formatMessage({ id: 'layout.moduleGate.phaseReason' }, { phase })
      : intl.formatMessage({ id: 'layout.moduleGate.phaseReasonUnknown' });
  }
  return intl.formatMessage(
    { id: 'layout.moduleGate.planReason' },
    { plan: reason.planName ?? reason.planCode },
  );
}
